import { useEffect, useMemo, useState } from "react";

import { api } from "../api";
import type { Section } from "../types";
import { useMainButton } from "../tg";
import ProgressRing from "../components/ProgressRing";
import { IconGear } from "./Admin";

type Props = {
  firstName: string | null;
  isAdmin: boolean;
  sections: Section[] | undefined;
  onSectionsLoaded: (s: Section[]) => void;
  onOpenParasites: () => void;
  // Generic-открытие секции через SectionLessons (для lessons/audio_course/daily_homework).
  onOpenSection: (section: Section) => void;
  // «Продолжить» с hero-карточки: открыть текущий урок секции сразу, минуя список.
  // Используется только для секции «Уроки» в HeroCard. Поиск конкретного урока — в App.
  onContinueSection: (section: Section) => void;
  onOpenAdmin: () => void;
  onOpenSettings: () => void;
};

// Подпись CTA для секций «Уроки» / «Аудио-курс».
// «Выполнить ДЗ» сейчас не показываем — для этого бэк ещё не отдаёт нужного флага.
function ctaLabel(sec: Section): string {
  if (sec.lessons_completed === 0) return "Начать";
  if (sec.lessons_completed < sec.lessons_total) return "Продолжить";
  return "Открыть";
}

// Текущий «номер» урока (1-based) для подписи "Урок N". Если всё пройдено — показываем последний.
function currentLessonNumber(sec: Section): number {
  if (sec.lessons_total === 0) return 1;
  return Math.min(sec.lessons_total, sec.lessons_completed + 1);
}

export default function Home({
  firstName,
  isAdmin,
  sections,
  onSectionsLoaded,
  onOpenParasites,
  onOpenSection,
  onContinueSection,
  onOpenAdmin,
  onOpenSettings,
}: Props) {
  const [error, setError] = useState<string | null>(null);

  useMainButton(null);

  // Тихий revalidate: всегда подтягиваем свежие данные в фоне, но спиннер показываем
  // только при первом заходе (когда sections === undefined). При возврате через свайп
  // sections уже в кеше — рендерим мгновенно, в фоне обновляем без визуального дёрга.
  useEffect(() => {
    let cancelled = false;
    api<Section[]>("/sections")
      .then((data) => {
        if (!cancelled) onSectionsLoaded(data);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [onSectionsLoaded]);

  // Доступ к секции по id — раскладка фиксированная (hero/feature/grid), а бэк
  // присылает массив. Берём по id, не по индексу, чтобы порядок в API не ломал UI.
  const byId = useMemo(() => {
    if (!sections) return null;
    return Object.fromEntries(sections.map((s) => [s.id, s])) as Record<
      string,
      Section | undefined
    >;
  }, [sections]);

  const greeting = firstName ? `Привет, ${firstName}!` : "Привет!";

  const lessons = byId?.["lessons"];
  const audio = byId?.["audio_course"];
  const parasites = byId?.["parasites"];
  const daily = byId?.["daily_homework"];

  return (
    <div className="screen">
      <header className="home-header">
        <div>
          <h1>Демосфен</h1>
          <p className="subtitle">{greeting}</p>
        </div>
        <div className="home-header-actions">
          {isAdmin && (
            <button
              type="button"
              className="home-admin-person-btn"
              onClick={onOpenAdmin}
              aria-label="Админка"
              title="Админка"
            >
              <IconAdminPerson />
            </button>
          )}
          <button
            type="button"
            className="home-settings-btn"
            onClick={onOpenSettings}
            aria-label="Настройки"
            title="Настройки"
          >
            <IconGear />
          </button>
        </div>
      </header>

      {error && !sections && (
        <div className="error-card">Не удалось загрузить разделы: {error}</div>
      )}

      {!sections && !error && <div className="card">Загрузка…</div>}

      {sections && (
        <div className="sections">
          {lessons && (
            <HeroCard
              section={lessons}
              onOpenAll={
                lessons.available ? () => onOpenSection(lessons) : undefined
              }
              onContinue={
                lessons.available ? () => onContinueSection(lessons) : undefined
              }
            />
          )}
          {audio && (
            <FeatureCard
              section={audio}
              onOpen={audio.available ? () => onOpenSection(audio) : undefined}
              onContinue={
                audio.available ? () => onContinueSection(audio) : undefined
              }
            />
          )}

          <div className="sections-grid">
            {parasites && (
              <SquareCard
                section={parasites}
                onOpen={parasites.available ? onOpenParasites : undefined}
              />
            )}
            {daily && (
              <SquareCard
                section={daily}
                onOpen={daily.available ? () => onOpenSection(daily) : undefined}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// --- Большая hero-карточка: «Уроки» -----------------------------------------

function HeroCard({
  section,
  onOpenAll,
  onContinue,
}: {
  section: Section;
  // Secondary CTA: «Все уроки» — открывает SectionLessons.
  onOpenAll?: () => void;
  // Primary CTA: «Начать»/«Продолжить»/«Открыть» — сразу текущий урок, минуя список.
  onContinue?: () => void;
}) {
  if (!section.available) {
    return (
      <div className="section-hero section-disabled">
        <SoonPill />
        <div className="section-hero-title">{section.title}</div>
      </div>
    );
  }
  const num = currentLessonNumber(section);
  const allClickable = !!onOpenAll;
  const continueClickable = !!onContinue;
  return (
    <div className="section-hero section-hero-grid">
      <div className="section-hero-content">
        <div className="section-hero-title">{section.title}</div>
        <div className="section-hero-meta">
          Урок {num} · {section.progress_pct}% пройдено
        </div>
      </div>
      <div className="section-hero-ring">
        <ProgressRing percent={section.progress_pct} size={60} stroke={6}>
          <span className="section-hero-ring-ratio">
            {section.lessons_completed}/{section.lessons_total}
          </span>
        </ProgressRing>
      </div>
      <div className="section-hero-actions">
        <button
          type="button"
          className="section-cta section-hero-cta-secondary"
          onClick={onOpenAll}
          disabled={!allClickable}
        >
          Все уроки
        </button>
        <button
          type="button"
          className="section-cta"
          onClick={onContinue}
          disabled={!continueClickable}
        >
          {ctaLabel(section)}
        </button>
      </div>
    </div>
  );
}

// --- Средняя feature-карточка: «Аудио-курс» ---------------------------------
// Дизайн B1 из AdminUITest sandbox: ProgressRing 56/6 слева с inline X/M,
// в центре заголовок + meta, справа большая круглая кнопка ▶.
// Тап на карточку → SectionLessons (список уроков), тап на ▶ → текущий урок.

function FeatureCard({
  section,
  onOpen,
  onContinue,
}: {
  section: Section;
  // Открыть список уроков секции.
  onOpen?: () => void;
  // Перейти сразу к текущему незавершённому уроку (минуя список).
  onContinue?: () => void;
}) {
  if (!section.available) {
    return (
      <div className="section-feature section-disabled">
        <div className="section-feature-text">
          <div className="section-feature-title">{section.title}</div>
        </div>
        <SoonPill />
      </div>
    );
  }
  const openClickable = !!onOpen;
  const continueClickable = !!onContinue;
  const meta = `${section.lessons_completed}/${section.lessons_total} пройдено`;
  // Карточка — div, а не button, потому что внутри лежит настоящий <button>
  // (play). Вложение button-в-button — невалидный HTML.
  return (
    <div
      className="section-feature section-feature-audio"
      role={openClickable ? "button" : undefined}
      tabIndex={openClickable ? 0 : undefined}
      onClick={openClickable ? onOpen : undefined}
      onKeyDown={
        openClickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onOpen?.();
              }
            }
          : undefined
      }
      aria-disabled={!openClickable}
    >
      <ProgressRing percent={section.progress_pct} size={56} stroke={6}>
        <span className="section-feature-ring-ratio">
          {section.lessons_completed}/{section.lessons_total}
        </span>
      </ProgressRing>
      <div className="section-feature-text">
        <div className="section-feature-title">{section.title}</div>
        <div className="section-feature-meta">{meta}</div>
      </div>
      <button
        type="button"
        className="section-feature-play-btn"
        onClick={(e) => {
          // Не пробрасывать клик в родительскую карточку (которая ведёт в список).
          e.stopPropagation();
          onContinue?.();
        }}
        disabled={!continueClickable}
        aria-label={ctaLabel(section)}
      >
        ▶
      </button>
    </div>
  );
}

// --- Квадратная карточка с кольцом: «Слова-паразиты», «Ежедневное ДЗ» -------

function SquareCard({
  section,
  onOpen,
}: {
  section: Section;
  onOpen?: () => void;
}) {
  const clickable = section.available && !!onOpen;

  if (!section.available) {
    return (
      <div className="section-square section-disabled">
        <ProgressRing percent={0} size={104}>
          <span className="ring-soon">Скоро</span>
        </ProgressRing>
        <div className="section-square-title">{section.title}</div>
      </div>
    );
  }

  const ratio = `${section.lessons_completed}/${section.lessons_total}`;
  const isDone =
    section.lessons_total > 0 &&
    section.lessons_completed >= section.lessons_total;
  return (
    <button
      type="button"
      className="section-square"
      onClick={clickable ? onOpen : undefined}
      disabled={!clickable}
    >
      <ProgressRing
        percent={section.progress_pct}
        size={104}
        color={isDone ? "#3aa05a" : undefined}
      >
        <span className="ring-ratio">{ratio}</span>
      </ProgressRing>
      <div className="section-square-title">{section.title}</div>
    </button>
  );
}

// --- Мелкая «Скоро» pill ----------------------------------------------------

function SoonPill() {
  return <span className="section-soon-pill">Скоро</span>;
}

// --- Иконка «админ»: силуэт человека в круге --------------------------------

function IconAdminPerson() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="12" cy="8" r="4" fill="currentColor" />
      <path
        d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}
